import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import type { InterpolationPreview } from '~/engine/util/algorithms/sdf/ContourOperations';
import { formatMmAsCmAtScale } from '~/utils/units';

export type InterpolationScope = 'allEmpty' | 'current';

/**
 * Maps the read-only interpolation preview to the human-readable read-out
 * shown in the panel. Distances are converted from the domain unit (mm) to the
 * UI unit (cm) here, at the UI boundary. Pure (no engine runtime imports) so it
 * is cheap to unit-test in isolation.
 */
export function renderPreviewMessage(
    preview: InterpolationPreview,
    scope: InterpolationScope
): ReactNode {
    switch (preview.status) {
        case 'fills':
            if (scope === 'current') {
                return (
                    <FormattedMessage
                        id={
                            preview.mode === 'copy'
                                ? 'interpolation.preview.copiesCurrent'
                                : 'interpolation.preview.fillsCurrent'
                        }
                        values={{
                            z: formatMmAsCmAtScale(preview.startZMm, 'macro'),
                        }}
                    />
                );
            }
            if (preview.sliceCount === 1) {
                return (
                    <FormattedMessage
                        id="interpolation.preview.fillsOne"
                        values={{
                            z: formatMmAsCmAtScale(preview.startZMm, 'macro'),
                        }}
                    />
                );
            }
            return (
                <FormattedMessage
                    id="interpolation.preview.fillsMany"
                    values={{
                        count: preview.sliceCount,
                        start: formatMmAsCmAtScale(preview.startZMm, 'macro'),
                        end: formatMmAsCmAtScale(preview.endZMm, 'macro'),
                    }}
                />
            );
        case 'alreadyContoured':
            return (
                <FormattedMessage id="interpolation.preview.alreadyContoured" />
            );
        case 'noNeighbors':
            return <FormattedMessage id="interpolation.preview.noNeighbors" />;
        case 'notASlicePlane':
            return (
                <FormattedMessage id="interpolation.preview.notASlicePlane" />
            );
        case 'nothingToFill':
        case 'outOfRange':
        case 'unavailable':
        default:
            return (
                <FormattedMessage id="interpolation.preview.nothingToFill" />
            );
    }
}
